import React, { useState, useEffect, useCallback } from "react";
import { useApp } from "../context/AppContext";
import api from "../lib/api";
import { Card, Button, Input, Modal } from "./ui";
import { formatMoney } from "../lib/utils";
import { ACTIVITY_TYPES, TRADE_TYPES, activityTypeLabel } from "../lib/portfolio";
import {
  num, Labeled, AccountSelect, CurrencyField, TradeFields, SplitFields, DividendFields,
  InterestFields, CashAmountField, TransferFields,
} from "./ActivityForms";
import {
  Plus, Pencil, Trash2, TrendingUp, TrendingDown, ArrowDownLeft,
  ArrowUpRight, Coins, ArrowLeftRight, Split, Receipt, Percent, Landmark,
} from "lucide-react";

const ICONS = {
  buy: TrendingUp, sell: TrendingDown, deposit: ArrowDownLeft, withdrawal: ArrowUpRight,
  dividend: Coins, transfer: ArrowLeftRight, split: Split, fee: Receipt,
  interest: Percent, tax: Landmark,
};
const POS = "#10B981";
const NEG = "#EF4444";

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

// Initial form state for the activity modal (new or edit).
function initActivityForm(activity, accounts, defaultAccountId) {
  return {
    type: activity?.type || "buy",
    account_id: activity?.account_id || defaultAccountId || (accounts[0]?.id || ""),
    date: activity?.date || todayISO(),
    symbol: activity?.symbol || "",
    name: activity?.name || "",
    asset_type: activity?.asset_type || "stock",
    quantity: activity?.quantity ?? "",
    price: activity?.price ?? "",
    fee: activity?.fee ?? "",
    amount: activity?.amount ?? "",
    ratio: activity?.ratio ?? "",
    notes: activity?.notes || "",
    from_account_id: activity?.from_account_id || (accounts[0]?.id || ""),
    to_account_id: activity?.to_account_id || (accounts[1]?.id || ""),
    transfer_kind: activity?.transfer_kind || "cash",
    is_external: activity?.is_external || false,
    ext_dir: activity?.from_account_id && !activity?.to_account_id ? "out" : "in",
    dividend_type: activity?.dividend_type || "cash",
    interest_type: activity?.interest_type || "cash",
    option_side: activity?.option_side || "call",
    strike: activity?.strike ?? "",
    expiration: activity?.expiration || "",
    coupon: activity?.coupon ?? "",
    currency: activity?.currency
      || accounts.find((a) => a.id === (activity?.account_id || accounts[0]?.id))?.currency
      || "EUR",
  };
}

// Build the API payload for an activity from form state (per activity type).
function buildActivityPayload(entity, form) {
  const isTrade = TRADE_TYPES.includes(form.type);
  const isOption = isTrade && form.asset_type === "option";
  const isSplit = form.type === "split";
  const isPlainCash = ["deposit", "withdrawal", "fee", "tax"].includes(form.type);
  const sym = form.symbol ? form.symbol.trim().toUpperCase() : null;
  const nm = form.name ? form.name.trim() : null;
  const payload = {
    entity_id: entity.id, type: form.type, date: form.date, notes: form.notes || null,
    account_id: form.type === "transfer" ? null : (form.account_id || null),
    currency: isSplit ? null : (form.currency || null),
  };
  if (isTrade) {
    Object.assign(payload, {
      symbol: sym, name: nm, asset_type: form.asset_type,
      quantity: num(form.quantity), price: num(form.price), fee: num(form.fee),
    });
    if (isOption) Object.assign(payload, {
      option_side: form.option_side, strike: num(form.strike), expiration: form.expiration || null,
    });
    if (form.asset_type === "bond") Object.assign(payload, {
      coupon: num(form.coupon), expiration: form.expiration || null,
    });
  } else if (isSplit) {
    Object.assign(payload, { symbol: sym, name: nm, ratio: num(form.ratio) });
  } else if (form.type === "dividend") {
    payload.dividend_type = form.dividend_type;
    payload.symbol = sym; payload.name = nm;
    if (form.dividend_type === "cash") payload.amount = num(form.amount);
    else { payload.quantity = num(form.quantity); payload.price = num(form.price); }
  } else if (form.type === "interest") {
    payload.interest_type = form.interest_type;
    payload.symbol = sym; payload.amount = num(form.amount);
  } else if (form.type === "transfer") {
    payload.transfer_kind = form.transfer_kind;
    payload.is_external = form.is_external;
    if (form.is_external) {
      if (form.ext_dir === "in") payload.to_account_id = form.account_id || null;
      else payload.from_account_id = form.account_id || null;
    } else {
      payload.from_account_id = form.from_account_id || null;
      payload.to_account_id = form.to_account_id || null;
    }
    if (form.transfer_kind === "securities") { payload.symbol = sym; payload.quantity = num(form.quantity); }
    else payload.amount = num(form.amount);
  } else if (isPlainCash) {
    payload.amount = num(form.amount);
  }
  return payload;
}

// The 10 activity-type picker tiles (buy/sell/deposit/…/tax).
function ActivityTypeGrid({ form, set, lang }) {
  return (
    <div className="grid grid-cols-5 gap-2" data-testid="activity-type-grid">
      {ACTIVITY_TYPES.map((tp) => {
        const Ico = ICONS[tp.value];
        const active = form.type === tp.value;
        return (
          <button key={tp.value} onClick={() => set("type", tp.value)}
            data-testid={`activity-type-${tp.value}`}
            className={`flex flex-col items-center gap-1 py-2 rounded-lg border text-[11px] transition-all ${active ? "text-text-primary" : "text-text-secondary hover:text-text-primary"}`}
            style={active ? { borderColor: "#2563EB", background: "#2563EB1a" } : {}}>
            <Ico className="w-4 h-4" />
            {lang === "fr" ? tp.label.fr : tp.label.en}
          </button>
        );
      })}
    </div>
  );
}

export default function ActivityLedger({ entity, accounts, onChange, refreshKey }) {
  const { t, lang, canEdit, toast } = useApp();
  const [items, setItems] = useState([]);
  const [modal, setModal] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/portfolio/activities?entity_id=${entity.id}`);
      setItems(data);
    } catch {
      setItems([]);
    }
  }, [entity.id]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const doDelete = async () => {
    if (!pendingDelete) return;
    const id = pendingDelete.id;
    setPendingDelete(null);
    try {
      await api.delete(`/portfolio/activities/${id}`);
    } catch (e) {
      // 404 → the activity is already gone (e.g. after a reset); just resync.
      if (e?.response?.status !== 404) {
        toast(t("activity_delete_error"));
        return;
      }
    }
    toast(t("activity_deleted"));
    load();
    onChange?.();
  };

  const accName = (id) => accounts.find((a) => a.id === id)?.name || "—";
  const acctCurrency = (id) => accounts.find((a) => a.id === id)?.currency || (entity.country === "US" ? "USD" : "EUR");

  const detail = (a) => {
    if (TRADE_TYPES.includes(a.type)) {
      const net = (Number(a.quantity) || 0) * (Number(a.price) || 0);
      return { text: `${a.quantity ?? 0} × ${formatMoney(a.price, acctCurrency(a.account_id), { decimals: 2 })}`, amount: net, sign: a.type === "buy" ? -1 : 1 };
    }
    if (a.type === "split") return { text: `${a.quantity ?? 0}:1`, amount: null, sign: 0 };
    const sign = ["withdrawal", "fee", "tax"].includes(a.type) ? -1 : 1;
    return { text: "", amount: Number(a.amount) || 0, sign };
  };

  return (
    <Card className="mt-5" data-testid="activity-ledger">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Receipt className="w-4 h-4 text-text-secondary" />
          <h3 className="font-heading font-semibold">{t("activity_title")}</h3>
          <span className="text-xs text-text-secondary">{items.length}</span>
        </div>
        {canEdit && (
          <Button size="sm" onClick={() => setModal({})} data-testid="add-activity-button">
            <Plus className="w-4 h-4 mr-1" /> {t("activity_add")}
          </Button>
        )}
      </div>

      {items.length === 0 ? (
        <p className="text-sm text-text-secondary py-4">{t("activity_none")}</p>
      ) : (
        <div className="space-y-1.5">
          {items.map((a) => {
            const Ico = ICONS[a.type] || Receipt;
            const d = detail(a);
            return (
              <div key={a.id} className="rounded-lg border px-3 py-2.5 flex items-center gap-3 group text-sm" data-testid={`activity-${a.id}`}>
                <div className="w-7 h-7 rounded-full bg-surface-hover flex items-center justify-center shrink-0">
                  <Ico className="w-3.5 h-3.5" />
                </div>
                <div className="w-24 shrink-0 text-text-secondary text-xs tabular-nums">{a.date}</div>
                <div className="w-20 shrink-0 font-medium">{activityTypeLabel(a.type, lang)}</div>
                <div className="min-w-0 flex-1 truncate text-text-secondary">
                  {a.symbol && <span className="font-mono text-text-primary mr-1.5">{a.symbol}</span>}
                  {d.text}
                  <span className="ml-2 px-1.5 py-0.5 rounded-full border text-[10px]">{accName(a.account_id)}</span>
                </div>
                {d.amount !== null && (
                  <div className="text-right tabular-nums font-medium shrink-0"
                    style={{ color: d.sign > 0 ? POS : d.sign < 0 ? NEG : undefined }}>
                    {d.sign > 0 ? "+" : d.sign < 0 ? "−" : ""}
                    {formatMoney(Math.abs(d.amount), acctCurrency(a.account_id), { decimals: 2 })}
                  </div>
                )}
                {canEdit && (
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => setModal(a)} data-testid={`activity-edit-${a.id}`} className="text-text-secondary hover:text-text-primary"><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => setPendingDelete(a)} data-testid={`activity-delete-${a.id}`} className="text-text-secondary hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {modal && (
        <ActivityModal entity={entity} accounts={accounts}
          activity={modal.id ? modal : null}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); onChange?.(); }} />
      )}

      {pendingDelete && (
        <Modal open onClose={() => setPendingDelete(null)} title={t("delete")}
          footer={<>
            <Button variant="outline" onClick={() => setPendingDelete(null)} data-testid="activity-delete-cancel">{t("cancel")}</Button>
            <Button onClick={doDelete} className="!bg-red-500 hover:!bg-red-600" data-testid="activity-delete-confirm">{t("delete")}</Button>
          </>}>
          <p className="text-sm text-text-secondary">{t("activity_delete_confirm")}</p>
        </Modal>
      )}
    </Card>
  );
}

export function ActivityModal({ entity, accounts, activity, defaultAccountId, onClose, onSaved }) {
  const { t, lang } = useApp();
  const region = entity.country === "US" ? "US" : "FR";
  const [positions, setPositions] = useState([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/portfolio/summary?entity_id=${entity.id}`);
        if (alive) setPositions(data.holdings || []);
      } catch { if (alive) setPositions([]); }
    })();
    return () => { alive = false; };
  }, [entity.id]);

  const [form, setForm] = useState(() => initActivityForm(activity, accounts, defaultAccountId));
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // selecting an account preselects its currency (until manually overridden / symbol picked)
  const setAccount = (v) => setForm((f) => ({
    ...f, account_id: v,
    currency: accounts.find((a) => a.id === v)?.currency || f.currency,
  }));

  const isTrade = TRADE_TYPES.includes(form.type);
  const isOption = isTrade && form.asset_type === "option";
  const isSplit = form.type === "split";
  const isPlainCash = ["deposit", "withdrawal", "fee", "tax"].includes(form.type);

  const onSymbolPick = (r, price, cur) => {
    setForm((f) => ({
      ...f,
      symbol: r.symbol || f.symbol,
      name: f.name || r.name || "",
      price: (isTrade && (f.price === "" || f.price == null) && price != null) ? price : f.price,
      currency: cur || f.currency,
    }));
  };

  // pick a holding the user already owns → fill symbol/name/currency (+ price hint)
  const onHoldingPick = (p) => {
    setForm((f) => ({
      ...f,
      symbol: p.symbol || f.symbol,
      name: p.name || f.name,
      currency: p.currency || f.currency,
      price: (f.price === "" || f.price == null) && p.price != null
        ? Math.round(p.price * 100) / 100 : f.price,
    }));
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = buildActivityPayload(entity, form);
      if (activity) await api.put(`/portfolio/activities/${activity.id}`, payload);
      else await api.post("/portfolio/activities", payload);
      onSaved();
    } finally { setBusy(false); }
  };

  const sectionProps = { form, set, t, lang, region, onSymbolPick, positions, onHoldingPick };

  return (
    <Modal open onClose={onClose} title={activity ? t("activity_edit") : t("activity_add")}
      footer={<>
        <Button variant="outline" onClick={onClose} data-testid="activity-cancel">{t("cancel")}</Button>
        <Button onClick={save} disabled={busy} data-testid="activity-save">{busy ? t("loading") : t("save")}</Button>
      </>}>
      <div className="space-y-4">
        <ActivityTypeGrid form={form} set={set} lang={lang} />

        {form.type === "transfer" ? (
          <TransferFields {...sectionProps} accounts={accounts} />
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3">
              <Labeled label={t("portfolio_account")}>
                <AccountSelect accounts={accounts} value={form.account_id} onChange={setAccount} testid="activity-account-select" />
              </Labeled>
              <Labeled label={t("activity_date")}>
                <Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="activity-date-input" />
              </Labeled>
              <CurrencyField value={form.currency} onChange={(v) => set("currency", v)} />
            </div>
            {isTrade && <TradeFields {...sectionProps} />}
            {isSplit && <SplitFields {...sectionProps} />}
            {form.type === "dividend" && <DividendFields {...sectionProps} />}
            {form.type === "interest" && <InterestFields {...sectionProps} />}
            {isPlainCash && <CashAmountField form={form} set={set} t={t} />}
          </>
        )}

        <Labeled label={t("activity_notes")}>
          <Input value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="…" data-testid="activity-notes-input" />
        </Labeled>
      </div>
    </Modal>
  );
}
